<template>
  <div class="card timecard-page">
    <div class="card-body">
      <!-- Header -->
      <div class="tc-header">
        <div class="month-selector">
          <div class="year-selector">
            <button class="year-nav-btn" @click="changeYear(-1)"><i class="fas fa-chevron-left"></i></button>
            <span class="year-label">{{ currentDate.getFullYear() }}年</span>
            <button class="year-nav-btn" @click="changeYear(1)"><i class="fas fa-chevron-right"></i></button>
          </div>
          <div class="month-btn-group">
            <button v-for="m in 12" :key="m" :class="['month-btn', { active: currentDate.getMonth() === m - 1 }]" @click="selectMonth(m - 1)">{{ m }}月</button>
          </div>
        </div>
        <div class="tc-filters">
          <div class="view-toggle">
            <button :class="['toggle-btn', { active: viewMode === 'staff' }]" @click="viewMode = 'staff'"><i class="fas fa-user mr-1"></i>人別</button>
            <button :class="['toggle-btn', { active: viewMode === 'date' }]" @click="viewMode = 'date'"><i class="fas fa-calendar-alt mr-1"></i>日付順</button>
          </div>
          <button v-if="canEdit" class="btn btn-success btn-sm btn-add-timecard" @click="openCreate"><i class="fas fa-plus mr-1"></i>打刻を追加</button>
        </div>
        <div class="filter-section">
          <div class="filter-label"><i class="fas fa-filter mr-1"></i>ステータス</div>
          <div class="filter-btn-group">
            <button :class="['filter-btn', { active: filterStatus === '' }]" @click="filterStatus = ''">全て</button>
            <button :class="['filter-btn filter-btn-pending', { active: filterStatus === '承認待ち' }]" @click="filterStatus = filterStatus === '承認待ち' ? '' : '承認待ち'">承認待ち</button>
            <button :class="['filter-btn filter-btn-approved', { active: filterStatus === '承認済み' }]" @click="filterStatus = filterStatus === '承認済み' ? '' : '承認済み'">承認済み</button>
            <button :class="['filter-btn filter-btn-revision', { active: filterStatus === '修正依頼' }]" @click="filterStatus = filterStatus === '修正依頼' ? '' : '修正依頼'">修正依頼</button>
          </div>
        </div>
        <div class="filter-section">
          <div class="filter-label"><i class="fas fa-users mr-1"></i>従業員</div>
          <div class="filter-btn-group">
            <button :class="['filter-btn', { active: filterStaff === '' }]" @click="filterStaff = ''">全員</button>
            <button v-for="s in staffNames" :key="s" :class="['filter-btn filter-btn-staff', { active: filterStaff === s }]" @click="filterStaff = filterStaff === s ? '' : s">{{ s }}</button>
          </div>
        </div>
      </div>

      <!-- Staff view (default) -->
      <div v-if="viewMode === 'staff'">
        <div v-for="staffGroup in filteredStaffGroups" :key="staffGroup.name" class="staff-group">
          <div class="staff-group-header" @click="toggleStaffGroup(staffGroup.name)">
            <div class="staff-group-left">
              <i :class="['fas', expandedStaff[staffGroup.name] ? 'fa-chevron-down' : 'fa-chevron-right']" class="mr-2"></i>
              <span class="staff-group-name">{{ staffGroup.name }}</span>
              <span class="staff-group-count">{{ staffGroup.entries.length }}件</span>
            </div>
            <div class="staff-group-actions" @click.stop>
              <button v-if="staffGroup.pendingCount > 0" class="btn btn-sm btn-approve" @click="bulkApproveStaff(staffGroup, 2)">
                <i class="fas fa-check mr-1"></i>一括承認（{{ staffGroup.pendingCount }}）
              </button>
            </div>
          </div>
          <div v-show="expandedStaff[staffGroup.name]">
            <div class="table-responsive">
              <table class="table table-hover tc-table tc-table-nested">
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>種別</th>
                    <th>出勤</th>
                    <th>退勤</th>
                    <th>休憩</th>
                    <th>就業時間</th>
                    <th>残業</th>
                    <th>備考</th>
                    <th>ステータス</th>
                    <th class="th-actions">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in staffGroup.entries" :key="entry.data.id" @click="handleRowClick(entry)" :class="rowClass(entry)" class="clickable-row">
                    <td class="td-date">{{ entry.date }}</td>
                    <td><span :class="shiftTypeClass(entry)">{{ shiftTypeLabel(entry) }}</span></td>
                    <td>{{ entry.data.time_start }}</td>
                    <td><span v-if="entry.incomplete" class="badge-status badge-incomplete">未打刻</span><template v-else>{{ entry.data.time_stop }}</template></td>
                    <td>{{ entry.data.basic_break_hours }}</td>
                    <td :class="overtimeClass(entry)">{{ entry.data.working_hours }}</td>
                    <td :class="entry.data.overtime_hours ? 'td-overtime' : ''">{{ entry.data.overtime_hours || '' }}</td>
                    <td class="td-notes"><span v-if="entry.data.notes" class="notes-inline" :title="entry.data.notes"><i class="fas fa-comment-alt mr-1"></i>{{ truncateNotes(entry.data.notes) }}</span></td>
                    <td><span :class="statusClass(entry.data.approval_status)">{{ entry.data.approval_status }}</span></td>
                    <td class="td-actions" @click.stop>
                      <div class="action-btns">
                        <button v-if="canEdit" class="btn btn-sm btn-edit" @click="openEdit(entry)" title="編集"><i class="fas fa-pen"></i></button>
                        <button v-if="entry.data.approval_status === '承認待ち' && !entry.incomplete" class="btn btn-sm btn-approve" @click="inlineApprove(entry, 2)"><i class="fas fa-check"></i></button>
                        <button v-if="(entry.data.approval_status === '承認待ち' || entry.data.approval_status === '承認済み') && !entry.incomplete" class="btn btn-sm btn-revision" @click="inlineApprove(entry, 3)"><i class="fas fa-undo"></i></button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(entry.type, entry.data.id)" title="削除"><i class="fa fa-trash"></i></button>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <!-- Summary row -->
                <tfoot>
                  <tr class="summary-foot">
                    <td colspan="5" class="text-right"><strong>合計</strong></td>
                    <td><strong>{{ staffGroup.totalWorkHours }}</strong></td>
                    <td class="td-overtime"><strong>{{ staffGroup.totalOvertime }}</strong></td>
                    <td colspan="3">
                      <span class="summary-tag">出勤 {{ staffGroup.workDays }}日</span>
                      <span v-if="staffGroup.paidLeaveDays > 0" class="summary-tag tag-leave">有給 {{ staffGroup.paidLeaveDays }}日</span>
                      <span v-if="staffGroup.lateCount > 0" class="summary-tag tag-late">遅刻 {{ staffGroup.lateCount }}回</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div v-if="filteredStaffGroups.length === 0" class="text-center text-muted py-4">データがありません</div>
      </div>

      <!-- Date view -->
      <div v-if="viewMode === 'date'">
        <div v-for="dateGroup in filteredDateGroups" :key="dateGroup.date" class="date-group">
          <div class="date-group-header">
            <div>
              <i class="fas fa-calendar-day mr-2"></i>{{ dateGroup.date }}
              <span class="date-group-count">{{ dateGroup.entries.length }}件</span>
            </div>
            <div @click.stop>
              <button v-if="dateGroup.pendingCount > 0" class="btn btn-sm btn-approve" @click="bulkApproveDate(dateGroup, 2)">
                <i class="fas fa-check mr-1"></i>一括承認（{{ dateGroup.pendingCount }}）
              </button>
            </div>
          </div>
          <div class="table-responsive">
            <table class="table table-hover tc-table tc-table-nested">
              <thead>
                <tr>
                  <th>名前</th>
                  <th>種別</th>
                  <th>出勤</th>
                  <th>退勤</th>
                  <th>休憩</th>
                  <th>就業時間</th>
                  <th>残業</th>
                  <th>備考</th>
                  <th>ステータス</th>
                  <th class="th-actions">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in dateGroup.entries" :key="entry.data.id" @click="handleRowClick(entry)" :class="rowClass(entry)" class="clickable-row">
                  <td class="td-staff">{{ entry.staff }}</td>
                  <td><span :class="shiftTypeClass(entry)">{{ shiftTypeLabel(entry) }}</span></td>
                  <td>{{ entry.data.time_start }}</td>
                  <td><span v-if="entry.incomplete" class="badge-status badge-incomplete">未打刻</span><template v-else>{{ entry.data.time_stop }}</template></td>
                  <td>{{ entry.data.basic_break_hours }}</td>
                  <td :class="overtimeClass(entry)">{{ entry.data.working_hours }}</td>
                  <td :class="entry.data.overtime_hours ? 'td-overtime' : ''">{{ entry.data.overtime_hours || '' }}</td>
                  <td class="td-notes"><span v-if="entry.data.notes" class="notes-inline" :title="entry.data.notes"><i class="fas fa-comment-alt mr-1"></i>{{ truncateNotes(entry.data.notes) }}</span></td>
                  <td><span :class="statusClass(entry.data.approval_status)">{{ entry.data.approval_status }}</span></td>
                  <td class="td-actions" @click.stop>
                    <div class="action-btns">
                      <button v-if="canEdit" class="btn btn-sm btn-edit" @click="openEdit(entry)" title="編集"><i class="fas fa-pen"></i></button>
                      <button v-if="entry.data.approval_status === '承認待ち' && !entry.incomplete" class="btn btn-sm btn-approve" @click="inlineApprove(entry, 2)"><i class="fas fa-check"></i></button>
                      <button v-if="(entry.data.approval_status === '承認待ち' || entry.data.approval_status === '承認済み') && !entry.incomplete" class="btn btn-sm btn-revision" @click="inlineApprove(entry, 3)"><i class="fas fa-undo"></i></button>
                      <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(entry.type, entry.data.id)" title="削除"><i class="fa fa-trash"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-if="filteredDateGroups.length === 0" class="text-center text-muted py-4">データがありません</div>
      </div>
    </div>
  </div>

  <!-- Detail modal (承認済み) -->
  <div class="modal fade" id="modal-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header"><h5 class="modal-title">勤怠詳細</h5><button type="button" class="close" data-dismiss="modal"><span>&times;</span></button></div>
        <div class="modal-body">
          <table class="table table-borderless detail-table"><tbody>
            <tr><th>日付</th><td>{{ staff.date }}</td></tr>
            <tr><th>名前</th><td>{{ staff.staff }}</td></tr>
            <tr><th>出勤</th><td>{{ timecard.time_start }}</td></tr>
            <tr><th>退勤</th><td>{{ timecard.time_stop }}</td></tr>
            <tr><th>休憩</th><td>{{ timecard.basic_break_hours }}</td></tr>
            <tr><th>就業時間</th><td>{{ timecard.working_hours }}</td></tr>
            <tr><th>残業時間</th><td>{{ timecard.overtime_hours }}</td></tr>
            <tr><th>備考</th><td><div v-if="timecard.notes" class="notes-box"><i class="fas fa-comment-alt mr-1 text-muted"></i>{{ timecard.notes }}</div><span v-else class="text-muted">-</span></td></tr>
            <tr><th>位置情報（出勤）</th><td><a v-if="timecard.time_start_latitude" target="_blank" :href="'https://www.google.com/maps/search/'+timecard.time_start_latitude+',+'+timecard.time_start_longitude"><i class="fas fa-map-marker-alt mr-1"></i>地図を開く</a></td></tr>
            <tr><th>位置情報（退勤）</th><td><a v-if="timecard.time_stop_latitude" target="_blank" :href="'https://www.google.com/maps/search/'+timecard.time_stop_latitude+',+'+timecard.time_stop_longitude"><i class="fas fa-map-marker-alt mr-1"></i>地図を開く</a></td></tr>
          </tbody></table>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">閉じる</button>
          <button v-if="canEdit" type="button" class="btn btn-outline-primary btn-detail-edit" :disabled="!detailShown" @click="editFromDetail">編集する</button>
          <button type="button" class="btn btn-danger" @click="updateApproval(3, staff.type)">修正を依頼する</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Detail modal (承認待ち) -->
  <div class="modal fade" id="modal-2">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header"><h5 class="modal-title">承認待ち</h5><button type="button" class="close" data-dismiss="modal"><span>&times;</span></button></div>
        <div class="modal-body">
          <table class="table table-borderless detail-table"><tbody>
            <tr><th>日付</th><td>{{ staff.date }}</td></tr>
            <tr><th>名前</th><td>{{ staff.staff }}</td></tr>
            <tr><th>出勤</th><td>{{ timecard.time_start }}</td></tr>
            <tr><th>退勤</th><td>{{ timecard.time_stop }}</td></tr>
            <tr><th>休憩</th><td>{{ timecard.basic_break_hours }}</td></tr>
            <tr><th>就業時間</th><td>{{ timecard.working_hours }}</td></tr>
            <tr><th>残業時間</th><td>{{ timecard.overtime_hours }}</td></tr>
            <tr><th>備考</th><td><div v-if="timecard.notes" class="notes-box"><i class="fas fa-comment-alt mr-1 text-muted"></i>{{ timecard.notes }}</div><span v-else class="text-muted">-</span></td></tr>
          </tbody></table>
        </div>
        <div class="modal-footer">
          <button v-if="canEdit" type="button" class="btn btn-outline-primary btn-detail-edit" :disabled="!detailShown" @click="editFromDetail">編集する</button>
          <button type="button" class="btn btn-primary" @click="updateApproval(2, staff.type)">承認する</button>
          <button type="button" class="btn btn-danger" @click="updateApproval(3, staff.type)">修正を依頼する</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete modal -->
  <div class="modal fade" id="modal-delete">
    <div class="modal-dialog modal-sm modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center py-4"><i class="fas fa-exclamation-triangle text-danger mb-3" style="font-size: 2rem;"></i><p class="mb-1 font-weight-bold">本当に削除しますか？</p><p class="text-muted mb-0" style="font-size:13px;">この操作は取り消せません。</p></div>
        <div class="modal-footer justify-content-center">
          <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">キャンセル</button>
          <button type="button" class="btn btn-danger" @click="deleteRecord">削除する</button>
        </div>
      </div>
    </div>
  </div>

  <!-- 打刻の追加・修正（企業管理者のみ。canEdit のときだけ開ける） -->
  <TimecardEditModal
    v-if="editModal"
    :mode="editModal.mode"
    :timecard="editModal.timecard"
    :staff-options="staffOptions"
    :shop-options="shopOptions"
    :default-date="defaultDate"
    @close="editModal = null"
    @saved="onSaved"
  />
</template>

<script>
import { Modal } from 'bootstrap'
import TimecardEditModal from '../../components/TimecardEditModal.vue'
import { defaultEditDate } from '../../utils/timecardEdit'

// Bootstrap 5 ネイティブAPI経由でモーダル操作を行うヘルパー
const showModal = (id) => {
  const el = document.getElementById(id)
  if (el) Modal.getOrCreateInstance(el).show()
}
const hideModal = (id) => {
  const el = document.getElementById(id)
  if (el) Modal.getOrCreateInstance(el).hide()
}

export default {
  components: { TimecardEditModal },
  data() {
    return {
      data: [],
      timecard: [],
      currentDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      staff: [],
      shop: [],
      record: { id: null, type: null },
      company_plan: 0,
      navigatedDate: this.$moment(),
      viewMode: 'staff',
      filterStatus: '',
      filterStaff: '',
      expandedStaff: {},
      // 打刻の編集・追加ができるか。店舗モードではフロントが企業管理者と店舗管理者を
      // 区別できない（permission も Cookie の access も 3 になる）ため、一覧 API が返す値を使う
      canEdit: false,
      // 入力モーダル（{ mode: 'create' | 'edit', timecard }）。null なら閉じている
      editModal: null,
      // 詳細モーダル（承認済み・承認待ち）が開き切ったか。BS5 の Modal は開くアニメーション中の
      // hide() を無視するため、それまで「編集する」を押せないようにする（押せると詳細モーダルが
      // 閉じずに残り、既存の「閉じる」は data-dismiss のため効かず、利用者が閉じられなくなる）
      detailShown: false,
    }
  },
  computed: {
    staffNames() {
      const names = new Set();
      this.data.forEach(e => names.add(e.name));
      return [...names].sort((a, b) => a.localeCompare(b, 'ja'));
    },
    sortedData() {
      const allData = [];
      this.data.forEach(entry => {
        if (entry.timecard && entry.timecard.length > 0) {
          entry.timecard.forEach(tc => {
            if (!tc.time_start) return;
            // 退勤していない打刻（勤務中・退勤の打ち忘れ）は、直せる企業管理者にだけ出す。
            // 店舗管理者の画面は従来どおり出さない
            const incomplete = !tc.time_stop;
            if (incomplete && !this.canEdit) return;
            allData.push({
              type: 'timecard', date: tc.date, staff: entry.name, incomplete,
              regular_working_hours_start: entry.regular_working_hours_start,
              regular_working_hours_stop: entry.regular_working_hours_stop,
              working_hours: entry.regular_working_hours, data: tc
            });
          });
        }
      });
      allData.sort((a, b) => new Date(a.date) - new Date(b.date));
      return allData;
    },
    filteredData() {
      return this.sortedData.filter(entry => {
        if (this.filterStatus && entry.data.approval_status !== this.filterStatus) return false;
        if (this.filterStaff && entry.staff !== this.filterStaff) return false;
        return true;
      });
    },
    filteredDateGroups() {
      const groups = {};
      this.filteredData.forEach(entry => {
        const d = entry.date;
        if (!groups[d]) { groups[d] = { date: d, entries: [], pendingCount: 0 }; }
        groups[d].entries.push(entry);
        // 退勤していない打刻は承認させない（勤務中の打刻が承認されるのを防ぐ）
        if (entry.data.approval_status === '承認待ち' && !entry.incomplete) groups[d].pendingCount++;
      });
      return Object.values(groups);
    },
    filteredStaffGroups() {
      const groups = {};
      this.filteredData.forEach(entry => {
        if (!groups[entry.staff]) {
          groups[entry.staff] = { name: entry.staff, entries: [], pendingCount: 0, pendingIds: [] };
          if (this.expandedStaff[entry.staff] === undefined) this.expandedStaff[entry.staff] = true;
        }
        groups[entry.staff].entries.push(entry);
        if (entry.data.approval_status === '承認待ち' && !entry.incomplete) {
          groups[entry.staff].pendingCount++;
          groups[entry.staff].pendingIds.push({ type: entry.type, id: entry.data.id });
        }
      });
      // Calculate summaries
      Object.values(groups).forEach(g => {
        let totalMins = 0, otMins = 0, workDays = 0, lateCount = 0, paidLeaveDays = 0;
        g.entries.forEach(e => {
          if (e.type !== 'timecard' || e.incomplete) return;
          workDays++;
          totalMins += this._timeToMins(e.data.working_hours);
          otMins += this._timeToMins(e.data.overtime_hours);
          // 遅刻チェック: 出勤時間 > 基本開始時間
          if (e.data.time_start && e.regular_working_hours_start) {
            if (e.data.time_start > e.regular_working_hours_start.substring(0, 5)) lateCount++;
          }
        });
        // 有給数（別途取得していないため0）
        g.totalWorkHours = this._minsToStr(totalMins);
        g.totalOvertime = otMins > 0 ? this._minsToStr(otMins) : '';
        g.workDays = workDays;
        g.lateCount = lateCount;
        g.paidLeaveDays = paidLeaveDays;
      });
      return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name, 'ja'));
    },
    isButtonDisabled() {
      if (this.company_plan === 0) {
        if (this.$moment().format('YYYY-MM-DD') > this.navigatedDate.format('YYYY-MM-DD')) return true;
        if (this.$moment().format('YYYY-MM-DD') > this.navigatedDate.format('YYYY-MM-05')) return true;
      }
      return false;
    },
    staffOptions() {
      return this.data.map(s => ({ id: s.id, name: s.name, break_time: s.break_time }));
    },
    shopOptions() {
      return [{ id: Number(this.$store.getters.shop), shop_name: this.$store.getters.shop_name }];
    },
    defaultDate() {
      return defaultEditDate(this.currentDate);
    },
  },
  mounted() {
    this.getTimecard(this.formatCurrentDate());
    this.detailModalListeners = [];
    ['modal-1', 'modal-2'].forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const onShown = () => { this.detailShown = true; };
      const onHide = () => { this.detailShown = false; };
      el.addEventListener('shown.bs.modal', onShown);
      el.addEventListener('hide.bs.modal', onHide);
      this.detailModalListeners.push({ el, onShown, onHide });
    });
  },
  beforeUnmount() {
    (this.detailModalListeners || []).forEach(({ el, onShown, onHide }) => {
      el.removeEventListener('shown.bs.modal', onShown);
      el.removeEventListener('hide.bs.modal', onHide);
    });
  },
  methods: {
    _timeToMins(str) {
      if (!str) return 0;
      const parts = str.split(':');
      return parseInt(parts[0] || 0) * 60 + parseInt(parts[1] || 0);
    },
    _minsToStr(mins) {
      if (mins <= 0) return '0:00';
      return Math.floor(mins / 60) + ':' + String(mins % 60).padStart(2, '0');
    },
    formatCurrentDate() {
      return this.currentDate.getFullYear() + '-' + (this.currentDate.getMonth()+1).toString().padStart(2, '0') + '-01';
    },
    changeYear(dir) {
      this.currentDate = new Date(this.currentDate.getFullYear() + dir, this.currentDate.getMonth(), 1);
      this.navigatedDate = this.$moment(this.currentDate);
      this.getTimecard(this.formatCurrentDate());
    },
    selectMonth(m) {
      this.currentDate = new Date(this.currentDate.getFullYear(), m, 1);
      this.navigatedDate = this.$moment(this.currentDate);
      this.getTimecard(this.formatCurrentDate());
    },
    getTimecard(date) {
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/timecard/' + shop_id + '/' + date)
      .then(response => {
        this.canEdit = response.data.data.can_edit_timecard === true;
        this.data = response.data.data.staff;
        this.company_plan = response.data.data.company_plan;
      })
      .catch(error => { console.log(error); });
    },
    toggleStaffGroup(name) {
      this.expandedStaff[name] = !this.expandedStaff[name];
      this.expandedStaff = { ...this.expandedStaff };
    },
    truncateNotes(notes) {
      if (!notes || !notes.length) return '';
      return notes.length <= 15 ? notes : notes.slice(0, 15) + '...';
    },
    statusClass(status) {
      if (status === '承認済み') return 'badge-status badge-approved';
      if (status === '承認待ち') return 'badge-status badge-pending';
      if (status === '修正依頼') return 'badge-status badge-revision';
      return 'badge-status';
    },
    shiftTypeLabel(entry) {
      if (entry.data.working_hours) {
        const mins = this._timeToMins(entry.data.working_hours);
        const regularMins = this._timeToMins(entry.working_hours);
        if (regularMins > 0 && mins < regularMins * 0.7) return '半日';
      }
      return '通常';
    },
    shiftTypeClass(entry) {
      const label = this.shiftTypeLabel(entry);
      if (label === '半日') return 'badge-type badge-type-half';
      return 'badge-type badge-type-normal';
    },
    rowClass(entry) {
      const classes = [];
      // 遅刻
      if (entry.data.time_start && entry.regular_working_hours_start) {
        if (entry.data.time_start > entry.regular_working_hours_start.substring(0, 5)) classes.push('row-late');
      }
      // 残業
      if (entry.data.overtime_hours && this._timeToMins(entry.data.overtime_hours) > 0) classes.push('row-overtime');
      return classes.join(' ');
    },
    overtimeClass(entry) {
      if (entry.data.overtime_hours && this._timeToMins(entry.data.overtime_hours) >= 60) return 'td-overtime-heavy';
      return '';
    },
    inlineApprove(entry, status) {
      const url = process.env.VUE_APP_API_PATH + '/company/timecard/' + entry.data.id;
      this.$http.put(url, { approval_status: status })
      .then(response => {
        if (response.data.success) { this.$toast.success(response.data.message); this.getTimecard(this.formatCurrentDate()); }
      })
      .catch(error => { if (error.response) this.$toast.error(error.response.errors); });
    },
    async bulkApproveStaff(group, status) {
      if (!confirm(group.pendingCount + '件を一括承認しますか？')) return;
      for (const item of group.pendingIds) {
        try { await this.$http.put(process.env.VUE_APP_API_PATH + '/company/timecard/' + item.id, { approval_status: status }); } catch (e) { console.log(e); }
      }
      this.$toast.success(group.pendingCount + '件を承認しました');
      this.getTimecard(this.formatCurrentDate());
    },
    async bulkApproveDate(group, status) {
      const pending = group.entries.filter(e => e.data.approval_status === '承認待ち' && !e.incomplete);
      if (!confirm(pending.length + '件を一括承認しますか？')) return;
      for (const e of pending) {
        try { await this.$http.put(process.env.VUE_APP_API_PATH + '/company/timecard/' + e.data.id, { approval_status: status }); } catch (err) { console.log(err); }
      }
      this.$toast.success(pending.length + '件を承認しました');
      this.getTimecard(this.formatCurrentDate());
    },
    handleRowClick(data) {
      // 詳細モーダルは承認済み・承認待ちのときだけ（承認・修正依頼の操作を持つため）。
      // 退勤していない打刻は承認させないので開かない。それ以外（退勤していない・修正依頼・未申請）は、
      // 企業管理者なら編集画面を直接開く
      const status = data.data.approval_status;
      if (data.incomplete || (status !== '承認済み' && status !== '承認待ち')) {
        if (this.canEdit) this.openEdit(data);
        return;
      }
      this.staff = data;
      this.timecard = data.data;
      if (this.staff.data.approval_status === '承認済み') { showModal('modal-1'); }
      else if (this.staff.data.approval_status === '承認待ち') { showModal('modal-2'); }
    },
    updateApproval(status) {
      const url = process.env.VUE_APP_API_PATH + '/company/timecard/' + this.timecard.id;
      this.$http.put(url, { approval_status: status })
      .then(response => {
        if (response.data.success) { this.$toast.success(response.data.message); this.getTimecard(this.formatCurrentDate()); }
      })
      .catch(error => { if (error.response) this.$toast.error(error.response.errors); });
      hideModal('modal-1');
      hideModal('modal-2');
    },
    deleteRecord() {
      const url = process.env.VUE_APP_API_PATH + '/shop/timecard/' + this.record.id;
      this.$http.delete(url)
      .then(response => {
        this.$toast.success(response.data.message);
        hideModal('modal-delete');
        setTimeout(() => { this.getTimecard(this.formatCurrentDate()); }, 500);
      })
      .catch(error => {
        console.log(error);
        this.$toast.error('削除に失敗しました');
        hideModal('modal-delete');
      });
    },
    confirmDelete(type, id) { this.record.id = id; this.record.type = type; showModal('modal-delete'); },
    openCreate() {
      this.editModal = { mode: 'create', timecard: null };
    },
    openEdit(entry) {
      this.editModal = { mode: 'edit', timecard: entry.data };
    },
    // 詳細モーダルの「編集する」。詳細モーダルを閉じてから編集画面を開く
    editFromDetail() {
      hideModal('modal-1');
      hideModal('modal-2');
      this.openEdit(this.staff);
    },
    onSaved() {
      this.editModal = null;
      this.getTimecard(this.formatCurrentDate());
    },
  },
};
</script>

<style scoped>
.tc-header { margin-bottom: 16px; }
.month-selector { margin-bottom: 12px; }
.year-selector { display: flex; align-items: center; justify-content: center; gap: 12px; margin-bottom: 8px; }
.year-nav-btn { width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid #d2d6dc; background: #fff; color: #4a5568; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
.year-nav-btn:hover { background: #049C4C; border-color: #049C4C; color: #fff; }
.year-label { font-size: 20px; font-weight: 700; min-width: 80px; text-align: center; }
.month-btn-group { display: flex; flex-wrap: wrap; justify-content: center; gap: 4px; }
.month-btn { padding: 6px 12px; font-size: 13px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; min-width: 48px; }
.month-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.month-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }

.tc-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 10px 14px; background: #f7fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
.view-toggle { display: flex; border: 1.5px solid #d2d6dc; border-radius: 6px; overflow: hidden; }
.toggle-btn { padding: 6px 14px; font-size: 13px; font-weight: 600; border: none; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; }
.toggle-btn.active { background: #049C4C; color: #fff; }

.filter-section { margin-top: 8px; }
.filter-label { font-size: 11px; font-weight: 700; color: #718096; margin-bottom: 4px; padding-left: 2px; }
.filter-btn-group { display: flex; flex-wrap: wrap; gap: 5px; }
.filter-btn { padding: 5px 12px; font-size: 12px; font-weight: 600; border: 1.5px solid #d2d6dc; border-radius: 6px; background: #fff; color: #4a5568; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
.filter-btn:hover { background: #edf2f7; border-color: #a0aec0; }
.filter-btn.active { background: #049C4C; color: #fff; border-color: #049C4C; }
.filter-btn-pending.active { background: #d69e2e; border-color: #d69e2e; color: #fff; }
.filter-btn-approved.active { background: #38a169; border-color: #38a169; color: #fff; }
.filter-btn-revision.active { background: #e53e3e; border-color: #e53e3e; color: #fff; }
.filter-btn-staff.active { background: #3182ce; border-color: #3182ce; color: #fff; }

/* Table */
.tc-table { margin: 0; }
.tc-table thead th { font-size: 12px; font-weight: 700; color: #4a5568; background: #f7fafc; border-bottom: 2px solid #e2e8f0; padding: 10px 8px; white-space: nowrap; }
.tc-table tbody td { padding: 8px; font-size: 13px; vertical-align: middle; border-color: #edf2f7; }
.clickable-row { cursor: pointer; transition: background-color 0.15s; }
.clickable-row:hover { background-color: #f0faf4 !important; }
.td-date { font-weight: 600; color: #1a202c; white-space: nowrap; }
.td-staff { font-weight: 600; color: #2d3748; }
.td-notes { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #718096; font-size: 12px; }
.th-actions { width: 150px; text-align: center; }
.td-actions { width: 150px; text-align: center; }
.btn-add-timecard { margin-left: auto; }
.btn-edit { background: #e2e8f0; color: #2d3748; border: 1px solid #cbd5e0; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-edit:hover { background: #3182ce; color: #fff; border-color: #3182ce; }
.badge-incomplete { background: #e2e8f0; color: #4a5568; }
.action-btns { display: flex; gap: 3px; justify-content: center; }

/* Row highlighting */
.row-late { border-left: 3px solid #e53e3e !important; }
.row-overtime { background-color: #fffaf0 !important; }
.td-overtime { color: #d69e2e; font-weight: 600; }
.td-overtime-heavy { color: #e53e3e !important; font-weight: 700; }

/* Badges */
.badge-status { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 4px; white-space: nowrap; }
.badge-approved { background: #c6f6d5; color: #22543d; }
.badge-pending { background: #fefcbf; color: #744210; }
.badge-revision { background: #fed7d7; color: #9b2c2c; }

.badge-type { font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 3px; }
.badge-type-normal { background: #edf2f7; color: #4a5568; }
.badge-type-half { background: #fefcbf; color: #744210; }

.btn-approve { background: #c6f6d5; color: #22543d; border: 1px solid #9ae6b4; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-approve:hover { background: #049C4C; color: #fff; border-color: #049C4C; }
.btn-revision { background: #fefcbf; color: #744210; border: 1px solid #f6e05e; font-size: 11px; padding: 3px 7px; border-radius: 4px; transition: all 0.15s; }
.btn-revision:hover { background: #d69e2e; color: #fff; border-color: #d69e2e; }

/* Groups */
.staff-group, .date-group { border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 10px; overflow: hidden; }
.staff-group-header, .date-group-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: #f7fafc; cursor: pointer; user-select: none; border-bottom: 1px solid #e2e8f0; }
.staff-group-header:hover, .date-group-header:hover { background: #edf2f7; }
.staff-group-left { display: flex; align-items: center; gap: 4px; }
.staff-group-name { font-weight: 700; font-size: 14px; color: #1a202c; }
.staff-group-count, .date-group-count { font-size: 11px; color: #718096; font-weight: 600; background: #edf2f7; padding: 2px 7px; border-radius: 8px; margin-left: 6px; }
.staff-group-actions { display: flex; gap: 4px; }

/* Summary footer */
.summary-foot td { background: #f7fafc; font-size: 12px; border-top: 2px solid #e2e8f0; padding: 8px; }
.summary-tag { font-size: 11px; font-weight: 600; padding: 2px 6px; border-radius: 4px; background: #edf2f7; color: #2d3748; margin-right: 4px; }
.summary-tag.tag-leave { background: #fefcbf; color: #744210; }
.summary-tag.tag-late { background: #fed7d7; color: #9b2c2c; }

/* Notes & Detail */
.notes-inline { color: #4a5568; font-size: 11px; }
.notes-inline i { color: #a0aec0; }
.notes-box { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; font-size: 14px; }
.detail-table th { width: 35%; font-weight: 600; font-size: 13px; color: #4a5568; padding: 8px; border: none; }
.detail-table td { font-size: 14px; color: #1a202c; padding: 8px; border: none; }
.modal-title { font-weight: 700; }
</style>
